import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileText, Mail, RefreshCw, Sparkles } from "lucide-react";
import {
  adminListLlbTailoredNotes,
  adminRetryTailoredNoteEmail,
  requestLlbTailoredNotes,
} from "@/lib/api/llb-tutoring.functions";
import { Upload } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/llb-tailored-notes")({
  component: AdminLlbTailoredNotes,
});

function ts(s?: string | null) {
  return s ? new Date(s).toLocaleString("en-ZA") : "—";
}

function badge(s?: string | null) {
  const map: Record<string, string> = {
    sent: "bg-emerald-100 text-emerald-700",
    queued: "bg-sky-100 text-sky-700",
    failed: "bg-rose-100 text-rose-700",
    skipped: "bg-muted text-muted-foreground",
    ready: "bg-emerald-100 text-emerald-700",
    generating: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${map[s ?? ""] ?? "bg-muted text-muted-foreground"}`}>
      {s ?? "—"}
    </span>
  );
}

function AdminLlbTailoredNotes() {
  const listFn = useServerFn(adminListLlbTailoredNotes);
  const retryFn = useServerFn(adminRetryTailoredNoteEmail);
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [emailStatus, setEmailStatus] = useState<"any" | "queued" | "sent" | "failed" | "skipped">("any");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const q = useQuery({
    queryKey: ["admin-llb-tailored-notes", { search, moduleCode, emailStatus, from, to }],
    queryFn: () =>
      listFn({
        data: { search: search || undefined, moduleCode: moduleCode || undefined, emailStatus, from: from || undefined, to: to || undefined },
      }),
  });

  const rows = (q.data?.rows ?? []) as any[];

  async function retry(id: string) {
    try {
      await retryFn({ data: { id } });
      toast.success("Email re-queued.");
      qc.invalidateQueries({ queryKey: ["admin-llb-tailored-notes"] });
    } catch (e: any) {
      toast.error("Retry failed", { description: e?.message ?? String(e) });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> Tailored AI study notes — admin
          </h1>
          <p className="text-sm text-muted-foreground">
            Full content of every learner's tailored notes, per student per module, with email delivery status.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </header>

      <AdminUploadGenerate
        onCreated={() => qc.invalidateQueries({ queryKey: ["admin-llb-tailored-notes"] })}
      />

      <div className="rounded-xl border border-border bg-card p-3 grid gap-2 md:grid-cols-6">
        <Input className="md:col-span-2" placeholder="Search topic / student / email / module…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Input placeholder="Module code (e.g. PVL1501)" value={moduleCode} onChange={(e) => setModuleCode(e.target.value.toUpperCase())} />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={emailStatus}
          onChange={(e) => setEmailStatus(e.target.value as any)}
        >
          <option value="any">All email statuses</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tailored notes found.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <article key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm">
                    <span className="font-mono text-xs font-semibold text-navy">{r.module_code}</span>{" "}
                    <span className="font-medium">{r.topic}</span> {badge(r.status)} {badge(r.last_email_status)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.student?.full_name || r.student?.email || "Unknown student"}
                    {r.student?.unisa_student_number ? ` · UNISA ${r.student.unisa_student_number}` : ""} · {ts(r.created_at)}
                  </p>
                  {r.last_email_error ? (
                    <p className="text-[11px] text-rose-700">Email error: {r.last_email_error}</p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => retry(r.id)}>
                    <Mail className="h-3 w-3 mr-1" /> Resend email
                  </Button>
                </div>
              </div>
              {r.source_notes_md ? (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-amber-700 hover:text-amber-900">
                    <FileText className="mr-1 inline h-3 w-3" /> Student-supplied source notes ({r.source_notes_md.length.toLocaleString()} chars)
                  </summary>
                  <pre className="mt-2 max-h-[40vh] overflow-auto whitespace-pre-wrap rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-950">
                    {r.source_notes_md}
                  </pre>
                </details>
              ) : null}
              {r.content_md ? (
                <details className="mt-3" open>
                  <summary className="cursor-pointer text-xs text-navy hover:text-gold">
                    <FileText className="mr-1 inline h-3 w-3" /> View full AI-generated notes ({r.content_md.length.toLocaleString()} chars)
                  </summary>
                  <pre className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed">
                    {r.content_md}
                  </pre>
                </details>
              ) : null}

            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminUploadGenerate({ onCreated }: { onCreated: () => void }) {
  const generate = useServerFn(requestLlbTailoredNotes);
  const [moduleCode, setModuleCode] = useState("");
  const [topic, setTopic] = useState("");
  const [focus, setFocus] = useState("");
  const [sourceNotes, setSourceNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function onFile(file: File | null) {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error(`"${file.name}" is too large — max 1 MB for source notes (.txt / .md).`);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext && !["txt", "md", "markdown"].includes(ext)) {
      toast.error(`"${file.name}" — only .txt or .md source-note files are accepted here.`);
      return;
    }
    try {
      const text = await file.text();
      setSourceNotes((prev) => (prev ? `${prev}\n\n${text}` : text).slice(0, 20000));
      toast.success(`Loaded ${file.name} (${Math.round(file.size / 1024)} KB).`);
    } catch (e: any) {
      toast.error("Could not read file", { description: e?.message ?? String(e) });
    }
  }

  async function submit() {
    if (moduleCode.trim().length < 2 || topic.trim().length < 4) {
      toast.error("Module code and topic (≥4 chars) are required.");
      return;
    }
    if (sourceNotes.trim().length < 20) {
      toast.error("Paste or upload at least 20 characters of source notes to rewrite.");
      return;
    }
    setBusy(true);
    try {
      await generate({
        data: {
          moduleCode: moduleCode.trim().toUpperCase(),
          topic: topic.trim(),
          focus: focus.trim() || undefined,
          sourceNotesMd: sourceNotes.trim().slice(0, 20000),
        },
      });
      toast.success("Tailored notes generated. Appearing below.");
      setTopic("");
      setFocus("");
      setSourceNotes("");
      onCreated();
    } catch (e: any) {
      toast.error("Generation failed", { description: e?.message ?? String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="font-display text-lg text-navy flex items-center gap-2">
        <Upload className="h-4 w-4 text-gold" /> Upload notes → AI tailored notes
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Paste or upload raw notes (.txt / .md, ≤ 1 MB). Our AI will rewrite and expand them into exam-grade tailored notes (5–8 pages) for the chosen module. We do not write examinations, tests or assignments on behalf of students.
      </p>
      <div className="mt-3 grid gap-2 md:grid-cols-3">
        <Input
          placeholder="Module code (e.g. PVL1501)"
          value={moduleCode}
          onChange={(e) => setModuleCode(e.target.value.toUpperCase())}
          maxLength={20}
        />
        <Input
          className="md:col-span-2"
          placeholder="Topic (e.g. Vicarious liability)"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          maxLength={200}
        />
        <Input
          className="md:col-span-3"
          placeholder="Optional focus / specific questions"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          maxLength={800}
        />
      </div>
      <textarea
        className="mt-2 min-h-[140px] w-full rounded-md border border-input bg-background p-3 text-xs font-mono"
        placeholder="Paste source notes here (max 20,000 characters)…"
        value={sourceNotes}
        onChange={(e) => setSourceNotes(e.target.value.slice(0, 20000))}
        maxLength={20000}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 hover:bg-secondary/60">
          <Upload className="h-3 w-3" /> Upload .txt / .md
          <input
            type="file"
            accept=".txt,.md,.markdown,text/plain,text/markdown"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <span>{sourceNotes.length.toLocaleString()} / 20,000 chars</span>
        <Button size="sm" onClick={submit} disabled={busy}>
          <Sparkles className="h-3 w-3 mr-1" /> {busy ? "Generating…" : "Generate tailored notes"}
        </Button>
      </div>
    </section>
  );
}
